'use client';

import Link from 'next/link';
import type { ComponentPropsWithoutRef, ReactNode } from 'react';
import { trackCtaClicked, type CtaClickedProperties } from '@/lib/posthog';

export interface TrackedLinkProps extends ComponentPropsWithoutRef<typeof Link> {
  /**
   * When set, a `cta_clicked` event is fired on click. The object is
   * plain/serializable, which lets TrackedLink be used inside shared
   * components rendered from Server Components without crossing an RSC
   * function-prop boundary.
   */
  tracking?: CtaClickedProperties;
  children?: ReactNode;
}

/**
 * Thin client wrapper around Next.js `Link` that fires a PostHog
 * `cta_clicked` event on click when `tracking` is set.
 *
 * Use this in place of `<Link>` inside shared marketing components so the
 * component itself can stay a server component while still emitting
 * client-side CTA tracking events.
 */
export function TrackedLink({ tracking, children, onClick, ...linkProps }: TrackedLinkProps) {
  const handleClick: React.MouseEventHandler<HTMLAnchorElement> = (event) => {
    if (tracking) {
      trackCtaClicked(tracking);
    }
    if (onClick) {
      onClick(event);
    }
  };

  return (
    <Link {...linkProps} onClick={handleClick}>
      {children}
    </Link>
  );
}
